/**
 * A score, rendered once and the same way everywhere. SPEC.md §19, §20.3.
 *
 * Four screens showed a signed number with a positive/negative tint, and four of
 * them had their own copy of the tint map and their own idea of whether a screen
 * reader got the sign read out. One implementation instead: the glyph comes from
 * formatSigned, the spoken form from describeScore, and colour only ever
 * reinforces what the sign already says. §19
 */

import { describeScore, formatSigned } from '@card-counter/shared';

/** Positive/negative/level tint. Never the only signal — the sign carries it. §20.3 */
function scoreToneClass(value: number): string {
  if (value > 0) return 'text-positive';
  if (value < 0) return 'text-negative';
  return 'text-ink-muted';
}

export interface SignedScoreProps {
  value: number;
  /** Sizing and weight belong to the caller; tone and semantics belong here. */
  className?: string;
  /**
   * Set when the surrounding element already names the player and their score, so
   * the value is not announced twice.
   */
  silent?: boolean;
}

export function SignedScore({ value, className, silent = false }: SignedScoreProps) {
  const classes = ['shrink-0', scoreToneClass(value), className ?? ''].filter(Boolean).join(' ');

  return (
    <span data-numeric className={classes}>
      {/* "+12" reads as "plus twelve" only by luck, so the glyph is hidden and the
          spoken form is explicit. §19 */}
      <span aria-hidden="true">{formatSigned(value)}</span>
      {silent ? null : <span className="sr-only">{describeScore(value)}</span>}
    </span>
  );
}
